<template>
  <v-hover v-slot="{ hover }">
    <li
      :class="[
        `d-flex justify-space-between pa-6 my-2 assignments-list__item assignments-list__item-${renderAssignmentId(
          item
        )}`,
        isAssignmentActive(hover, item)
          ? 'white rounded-lg pointer'
          : 'transparent'
      ]"
      @click.stop="handleShowAssignmentItemDetails(item)"
    >
      <div class="d-flex align-center">
        <v-progress-circular
          :value="!isPlayer ? item.avgOfCompletion : item.progress"
          :rotate="-90"
          :color="renderAssignmentIconColor"
          :size="50"
        >
          <v-icon :color="renderAssignmentIconColor"
            >mdi-clipboard-text-outline
          </v-icon>
        </v-progress-circular>
        <div class="d-flex flex-column ml-5">
          <span class="subtitle-1 secondary--text font-weight-bold">
            {{ item.assignmentTitle }}
          </span>
          <span class="grey--text font-weight-medium" v-if="!isPlayer">
            {{ shareWith }}
          </span>
        </div>
      </div>
      <div class="d-flex align-center">
        <div class="d-flex flex-column align-end mr-2">
          <span
            class="subtitle-2 secondary--text font-weight-medium"
            v-if="!isPlayer"
          >
            {{ `${item.totalCompleted} of ${item.totalPlayers} Completed` }}
          </span>
          <span
            class="subtitle-2 secondary--text font-weight-medium"
            v-if="isPlayer"
          >
            {{ `${item.progress}% Completed` }}
          </span>

          <span
            class="grey--text font-weight-medium assignment-formatted"
            v-if="!isPlayer"
          >
            {{ assignOnFormatted }}
          </span>
          <span
            class="grey--text font-weight-medium"
            v-if="isPlayer"
            v-html="dueToFormatted"
          ></span>
        </div>
        <AssignmentsListItemContextMenu :assignment="item" />
      </div>
    </li>
  </v-hover>
</template>

<script>
import moment from "moment";
import AssignmentsListItemContextMenu from "./AssignmentsListItemContextMenu";
import { ModuleTypes } from "../../../store/ModuleTypes";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { StorageItemTypes } from "../enums/StorageItemTypes";
import { i18n } from "../../../plugins/i18n";
import { AssignmentStatusTypes } from "../enums/AssignmentStatusTypes";

export default {
  name: "AssignmentsListItem",
  components: { AssignmentsListItemContextMenu },
  props: {
    item: {
      type: Object,
      required: true
    },
    isPlayer: {
      type: Boolean,
      required: true
    },
    status: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapGetters(ModuleTypes.ASSIGNMENTS, ["assignmentItemDetails"]),

    shareWith: function() {
      let text = "";
      if (this.item.totalTeams) {
        const isTotalItemsPlural = this.item.totalTeams >= 2;
        text += `${this.item.totalTeams} ${
          isTotalItemsPlural ? i18n.tc("common.teams") : i18n.tc("common.team")
        }`;
      }
      if (this.item.totalPlayers) {
        const isTotalPlayersPlural = this.item.totalPlayers >= 2;
        if (text.length) {
          text += ", ";
        }
        text += `${this.item.totalPlayers} ${
          isTotalPlayersPlural
            ? i18n.tc("common.players")
            : i18n.tc("common.player")
        }`;
      }
      return text;
    },
    assignOnFormatted: function() {
      const date = moment(this.item.assignOn).format("MM/DD/YYYY");
      return `${i18n.tc("assignments.assigned")} ${date}`;
    },
    dueToFormatted: function() {
      const date = moment(this.item.dueDate).format("MM/DD/YYYY");
      const diffDays = moment(this.item.dueDate).diff(moment(), "days");
      if (diffDays < 0 && this.status !== AssignmentStatusTypes.COMPLETED) {
        return `<span class="red--text">Due ${diffDays * -1} ${
          diffDays < -1 ? "days" : "day"
        } ago</span>`;
      } else {
        return `${i18n.tc("assignments.due")} ${date}`;
      }
    },
    renderAssignmentIconColor: function() {
      if (this.isPlayer) {
        if (this.item.taskType === StorageItemTypes.TODO) {
          return "primary";
        } else if (this.item.taskType === StorageItemTypes.FORM) {
          return "#ff8b3c";
        }
      } else {
        if (this.item.type === StorageItemTypes.TODO) {
          return "primary";
        } else if (this.item.type === StorageItemTypes.FORM) {
          return "#ff8b3c";
        }
      }

      return "";
    }
  },

  methods: {
    ...mapActions(ModuleTypes.ASSIGNMENTS, [
      "showAssignmentItemDetails",
      "getPlayerAssignmentTask"
    ]),
    ...mapMutations(ModuleTypes.ROOT, ["openRightSidebar"]),
    ...mapMutations(ModuleTypes.ASSIGNMENTS, [
      "setAssignmentItemDetails",
      "toggleAssignmentTodoModal",
      "toggleAssignmentFormModal",
      "setIsPlayerTaskDetailsActive",
      "setActivePlayerTaskDetailsId"
    ]),
    handleShowAssignmentItemDetails(assignment) {
      this.setIsPlayerTaskDetailsActive({
        isPlayerTaskDetailsActive: false
      });
      this.setActivePlayerTaskDetailsId({
        activePlayerTaskDetailsId: null
      });
      this.setAssignmentItemDetails(assignment);
      if (this.isPlayer) {
        if (this.item.taskType === StorageItemTypes.TODO) {
          return this.toggleAssignmentTodoModal({
            isAssignmentTodoModalOpened: true,
            readOnly: this.status === AssignmentStatusTypes.COMPLETED
          });
        } else {
          return this.toggleAssignmentFormModal({
            isAssignmentFormModalOpened: true,
            readOnly: this.status === AssignmentStatusTypes.COMPLETED
          });
        }
      } else {
        this.openRightSidebar();
      }
    },

    isAssignmentActive: function(hover, item) {
      return (
        hover ||
        (item?.assignmentId &&
          item?.assignmentId === this.assignmentItemDetails?.assignmentId) ||
        (item?.playerResponseId &&
          item?.playerResponseId ===
            this.assignmentItemDetails?.taskPlayerResponseId)
      );
    },

    renderAssignmentId(item) {
      return this.$route.params?.playerId
        ? item?.playerResponseId
        : item?.assignmentId;
    }
  }
};
</script>

<style scoped>
.assignments-list__item:not(:last-child) {
  border-bottom: 1px solid var(--divider-color) !important;
}

.pointer {
  cursor: pointer;
}

@media (max-width: 980px) {
  .assignments-list__item {
    max-width: 90vw;
    font-size: 0.6rem;
  }
  .subtitle-1 {
    font-size: 0.7rem !important;
    line-height: 1rem !important;
    width: 7vw;
  }
  .subtitle-2 {
    font-size: 0.7rem !important;
    line-height: 1rem !important;
    margin-left: 2vw;
  }
  .assignment-formatted {
    margin-left: 2vw;
  }
}
</style>
